// Reconciles sports bets against a transaction reporting period.
//
// Transaction-based GGR for a period is computed from the money that moved
// inside that period. Sports bets break that assumption because a bet placed
// on June 30 can settle on July 2: the stake lands in June's numbers while the
// payout lands in July's. Agents then see July credits with no matching bet and
// ask where the money came from.
//
// This module finds the two kinds of bets that straddle a period boundary and
// quantifies how much they shift the period's GGR.

import { fetchAuthSession } from '@aws-amplify/auth';
import { apiPost } from './api';
import {
  formatDateForAPI,
  getTransactionTimestamp,
  normalizeProviderName,
  getTransactionProvider,
  isSportsTransaction,
  isBetConstructTrackerRow,
  getSportsGroupingId,
  summarizeRoundTransactions
} from './betTransactions';

// The analytics endpoint is queried per batch of players; anything larger than
// this makes the audit slow enough that it stops being a quick sanity check.
export const MAX_AUDIT_PLAYERS = 150;

// How far back before the period we look for the placement leg of bets that
// settled inside the period.
export const DEFAULT_LOOKBACK_DAYS = 45;

const PLAYER_BATCH_SIZE = 10;
const DAY_MS = 24 * 60 * 60 * 1000;

export const getAllPlayersUnderNode = (node) => {
  const players = [];
  const traverse = (currentNode) => {
    if (!currentNode) return;
    if (currentNode.type === 'player') {
      players.push(currentNode);
    }
    if (Array.isArray(currentNode.children)) {
      currentNode.children.forEach(traverse);
    }
  };
  traverse(node);
  return players;
};

export const findNodeInTree = (node, username) => {
  if (!node || !username) return null;
  if (node.username === username) return node;
  if (Array.isArray(node.children)) {
    for (const child of node.children) {
      const found = findNodeInTree(child, username);
      if (found) return found;
    }
  }
  return null;
};

// Fetch every sports game transaction for the given players in [start, end].
// Players are batched because the backend caps the number of playerIds it will
// scan in a single request.
export const fetchSportsTransactions = async ({ playerIds, start, end, onProgress }) => {
  if (!playerIds || playerIds.length === 0) return [];

  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();

  const batches = [];
  for (let i = 0; i < playerIds.length; i += PLAYER_BATCH_SIZE) {
    batches.push(playerIds.slice(i, i + PLAYER_BATCH_SIZE));
  }

  let completed = 0;
  const results = await Promise.all(
    batches.map(async (batch) => {
      const response = await apiPost('analytics', '/gametransactions', {
        playerIds: batch,
        startDate: formatDateForAPI(start),
        endDate: formatDateForAPI(end),
        platform: 'sport',
        limit: 10000
      }, token);

      completed += 1;
      if (onProgress) onProgress(Math.round((completed / batches.length) * 100));

      return Array.isArray(response?.data) ? response.data : [];
    })
  );

  return results.flat();
};

// Collapse raw sports transactions into one entry per bet, keeping the two
// timestamps the audit cares about: when the stake was taken and when the
// result was paid.
export const buildSportsBets = (transactions) => {
  const groups = new Map();

  transactions.forEach((tx) => {
    if (!isSportsTransaction(tx) || isBetConstructTrackerRow(tx)) return;
    const key = getSportsGroupingId(tx) || tx.transactionId;
    if (!key) return;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(tx);
  });

  return Array.from(groups.entries()).map(([betId, txs]) => {
    const sorted = [...txs].sort((a, b) => getTransactionTimestamp(a) - getTransactionTimestamp(b));
    const summary = summarizeRoundTransactions(sorted);
    const firstTx = sorted[0];

    const placementTimes = summary.bets.map(getTransactionTimestamp);
    const settlementTimes = [
      ...summary.wins,
      ...summary.settles,
      ...summary.resettles,
      ...summary.cancelSettles
    ].map(getTransactionTimestamp);

    const playerId = firstTx.playerId || null;

    return {
      betId: String(betId),
      playerId,
      // playerId carries a currency suffix (e.g. "john-USD"); the tree and the
      // transaction pages both key off the bare username.
      playerUsername: playerId ? String(playerId).split('-')[0] : null,
      currency: firstTx.currency || 'USD',
      provider: normalizeProviderName(getTransactionProvider(firstTx)),
      stake: summary.totalBet,
      payout: summary.totalWin + summary.totalSettle + summary.totalResettle + summary.totalCancelSettle,
      netResult: summary.netResult,
      settleResult: summary.settleResult,
      hasBet: summary.hasBet,
      hasSettlement: summary.hasSettlement,
      hasRollback: summary.hasRollback,
      placedAt: placementTimes.length ? Math.min(...placementTimes) : null,
      settledAt: settlementTimes.length ? Math.max(...settlementTimes) : null,
      transactions: sorted
    };
  });
};

const emptyTotals = () => ({
  carriedInCount: 0,
  carriedInStake: 0,
  carriedInPayout: 0,
  carriedOutCount: 0,
  carriedOutStake: 0,
  carriedOutPayout: 0,
  ggrShift: 0
});

// Classify bets relative to [rangeStart, rangeEnd]:
//
//  carriedIn  – placed before the period, settled inside it. The payout hits
//               this period's numbers but the stake was counted earlier.
//  carriedOut – placed inside the period, settled after it (or still open).
//               The stake is counted here but the payout is not.
//
// Bets fully inside the period, or fully outside it, are ignored: they do not
// distort the period.
export const auditSportsSettlements = ({ bets, rangeStart, rangeEnd }) => {
  const start = rangeStart.getTime();
  const end = rangeEnd.getTime();

  const carriedIn = [];
  const carriedOut = [];

  bets.forEach((bet) => {
    if (bet.hasRollback) return;

    const settledInRange =
      bet.hasSettlement && bet.settledAt !== null && bet.settledAt >= start && bet.settledAt <= end;

    // A missing placement leg means the bet was placed before the lookback
    // window, which still makes it a carried-in bet — just one whose stake we
    // cannot show.
    const placedBeforeRange = bet.placedAt === null || bet.placedAt < start;

    if (settledInRange && placedBeforeRange) {
      carriedIn.push({ ...bet, placementUnknown: bet.placedAt === null });
      return;
    }

    const placedInRange = bet.placedAt !== null && bet.placedAt >= start && bet.placedAt <= end;
    const settledAfterRange = !bet.hasSettlement || bet.settledAt === null || bet.settledAt > end;

    if (placedInRange && settledAfterRange) {
      carriedOut.push({ ...bet, stillOpen: !bet.hasSettlement });
    }
  });

  const byCurrency = {};
  const bump = (currency) => {
    if (!byCurrency[currency]) byCurrency[currency] = emptyTotals();
    return byCurrency[currency];
  };

  carriedIn.forEach((bet) => {
    const totals = bump(bet.currency);
    totals.carriedInCount += 1;
    totals.carriedInStake += bet.stake;
    totals.carriedInPayout += bet.payout;
  });

  carriedOut.forEach((bet) => {
    const totals = bump(bet.currency);
    totals.carriedOutCount += 1;
    totals.carriedOutStake += bet.stake;
    totals.carriedOutPayout += bet.payout;
  });

  // Period GGR measured from transactions differs from GGR measured against the
  // bets actually placed in the period by exactly this amount. Positive means
  // the period's transactions flatter the real result.
  Object.values(byCurrency).forEach((totals) => {
    totals.ggrShift = totals.carriedOutPayout - totals.carriedInPayout;
  });

  const sortByImpact = (a, b) => Math.abs(b.payout) - Math.abs(a.payout) || b.settledAt - a.settledAt;

  return {
    carriedIn: carriedIn.sort(sortByImpact),
    carriedOut: carriedOut.sort((a, b) => Math.abs(b.stake) - Math.abs(a.stake)),
    byCurrency,
    currencies: Object.keys(byCurrency).sort()
  };
};

// Roll a list of bets up per player so the modal can answer "which players and
// how much" without re-walking the raw transactions.
export const groupBetsByPlayer = (bets) => {
  const players = new Map();

  bets.forEach((bet) => {
    const key = bet.playerUsername || bet.playerId || 'unknown';
    if (!players.has(key)) {
      players.set(key, { username: key, bets: [], stake: 0, payout: 0, netResult: 0 });
    }
    const entry = players.get(key);
    entry.bets.push(bet);
    entry.stake += bet.stake;
    entry.payout += bet.payout;
    entry.netResult += bet.netResult;
  });

  return Array.from(players.values()).sort((a, b) => Math.abs(b.payout) - Math.abs(a.payout));
};

// The window we need to pull to see both legs of every straddling bet: far
// enough back to catch placements before the period, and up to now so that
// settlements which happened after the period are visible too.
export const getAuditFetchWindow = (rangeStart, rangeEnd, lookbackDays = DEFAULT_LOOKBACK_DAYS) => {
  const start = new Date(rangeStart.getTime() - lookbackDays * DAY_MS);
  const end = new Date(Math.max(rangeEnd.getTime(), Date.now()));
  return { start, end };
};

// Deep link into BetHistory pointing at one specific bet. The explicit
// from/to window is needed because the sports tab otherwise only loads the
// last 60 days, which can exclude an older placement.
export const buildBetHistoryLink = (bet) => {
  const anchor = bet.placedAt || bet.settledAt || Date.now();
  const from = new Date(anchor - 2 * DAY_MS);
  const to = new Date(Math.max(bet.settledAt || 0, Date.now()) + DAY_MS);

  const params = new URLSearchParams({
    tab: 'sport',
    bet: bet.betId,
    from: from.toISOString(),
    to: to.toISOString()
  });

  return `/bet-history?${params.toString()}`;
};
